import { Client as ChatClient } from "@twilio/conversations";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect } from "react";
import { parseJwt } from "../../auth/utils";
import { logger } from "../../datadog/logger";
import {
  reverseProxyWebsockets,
  twilioIdentityAtom,
  usedTwilioWebsocketsSuccessfullyAtom,
  usingWebsocketsReverseProxyAtom,
} from "../../video-sdk/twilio";
import {
  chatClientConnectionStateAtom,
  chatClientStateAtom,
  twilioChatClientAtom,
  twilioChatConnectionAttemptCountAtom,
} from "../state";
import { getMaxTwilioWebsocketConnectionRetriesBeforeFallback } from "../sync/utils";
import { ConnectionState, ConnectionStateStatus } from "../types";

export function useTeardownTwilioChatClientCallback() {
  const setTwilioChatClient = useSetAtom(twilioChatClientAtom);
  const getTwilioChatClient = useAtomCallback(
    useCallback((get) => get(twilioChatClientAtom), []),
  );
  const setChatClientState = useSetAtom(chatClientStateAtom);
  const setChatClientConnectionState = useSetAtom(
    chatClientConnectionStateAtom,
  );

  return useCallback(
    function teardownTwilioSyncClient() {
      const client = getTwilioChatClient();
      if (!client) return;
      client.removeAllListeners();
      client.shutdown();
      setTwilioChatClient(null);
      setChatClientState(null);
      setChatClientConnectionState(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

type InitTwilioChatClientOpts = {
  twilioChatToken?: string;
  onConnectionStateChanged?: (newState: ConnectionStateStatus) => void;
};

export function useInitTwilioChatClientCallback() {
  const setTwilioChatClient = useSetAtom(twilioChatClientAtom);
  const setChatClientConnectionState = useSetAtom(
    chatClientConnectionStateAtom,
  );
  const setChatClientState = useSetAtom(chatClientStateAtom);
  const setTwilioIdentity = useSetAtom(twilioIdentityAtom);
  const teardownTwilioChatClient = useTeardownTwilioChatClientCallback();
  const setUsingWebsocketsReverseProxy = useSetAtom(
    usingWebsocketsReverseProxyAtom,
  );
  const setUsedTwilioWsSuccessfully = useSetAtom(
    usedTwilioWebsocketsSuccessfullyAtom,
  );
  const getUsingWebsocketsReverseProxy = useAtomCallback(
    useCallback((get) => get(usingWebsocketsReverseProxyAtom), []),
  );
  const getUsedTwilioWsSuccessfully = useAtomCallback(
    useCallback((get) => get(usedTwilioWebsocketsSuccessfullyAtom), []),
  );
  const setTwilioChatConnectionAttemptCount = useSetAtom(
    twilioChatConnectionAttemptCountAtom,
  );
  const getTwilioChatConnectionAttemptCount = useAtomCallback(
    useCallback((get) => get(twilioChatConnectionAttemptCountAtom), []),
  );

  return useCallback(
    function initTwilioChatClient({
      twilioChatToken,
      onConnectionStateChanged,
    }: Omit<InitTwilioChatClientOpts, "twilioChatToken"> & {
      twilioChatToken: string;
    }) {
      teardownTwilioChatClient();

      if (getUsingWebsocketsReverseProxy()) {
        reverseProxyWebsockets();
      }

      const client = new ChatClient(twilioChatToken);
      logger.info("Initializing twilio chat client");

      client.on("tokenExpired", () => {
        logger.warn("Twilio chat token expired");
      });

      client.on("tokenAboutToExpire", () => {
        logger.warn("Twilio chat token about to expire");
      });

      client.on("connectionError", (connectionError) => {
        setTwilioChatConnectionAttemptCount((prevCount) => prevCount + 1);

        // Leaving WS fallback to the Sync client if retries are set
        const shouldSkipTwilioWsFallback =
          getMaxTwilioWebsocketConnectionRetriesBeforeFallback() > 0;
        const shouldUseTwilioSyncWsFallback =
          !shouldSkipTwilioWsFallback &&
          !getUsedTwilioWsSuccessfully() &&
          !getUsingWebsocketsReverseProxy();

        if (shouldUseTwilioSyncWsFallback) {
          setUsingWebsocketsReverseProxy(true);
        } else {
          logger.error(
            `Twilio chat connection error: ${connectionError.message}`,
          );
          setChatClientConnectionState("error");
        }

        logger.error(
          `Twilio chat connection error: ${connectionError.message}`,
          {
            connectionError,
            connectionAttemptCount: getTwilioChatConnectionAttemptCount(),
          },
        );
      });

      client.on("connectionStateChanged", (newState) => {
        logger.debug("Twilio chat connection state change", {
          twilioChatState: newState,
        });
        if (newState === ConnectionState.CONNECTED) {
          setUsedTwilioWsSuccessfully(true);
          setTwilioChatConnectionAttemptCount(0);
        }
        setChatClientConnectionState(newState);
        onConnectionStateChanged?.(newState);
      });

      client.on("stateChanged", (newState) => {
        logger.debug("Twilio chat state change", { twilioChatState: newState });
        setChatClientState(newState);
      });

      const identity = parseJwt<{ grants: { identity: string } }>(
        twilioChatToken,
      )?.grants?.identity;
      setTwilioChatClient(client);
      setTwilioIdentity(identity);
      return client;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

export function useInitTwilioChatClient(opts: InitTwilioChatClientOpts) {
  const { twilioChatToken } = opts;
  const initTwilioChatClient = useInitTwilioChatClientCallback();
  const teardownTwilioChatClient = useTeardownTwilioChatClientCallback();
  const usingWebsocketsReverseProxy = useAtomValue(
    usingWebsocketsReverseProxyAtom,
  );

  useEffect(
    function handleInitTwilioChatClient() {
      if (!twilioChatToken) return;
      initTwilioChatClient({
        ...opts,
        twilioChatToken,
      });
      return teardownTwilioChatClient;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [twilioChatToken, usingWebsocketsReverseProxy],
  );
}
